import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import { Plus, PencilSimple, Trash, Plant, CurrencyCircleDollar, Basket, Handshake, ChartBar, Ruler } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EMPTY_CROP, HARVEST_UNIT_OPTIONS, today, fmt } from "./constants";


export function ExpensesTab({ crops, expenses, categoriesList, reload }) {
  const [form, setForm] = useState({ crop_id: "", date: today(), category: "sulama", amount: "", description: "" });
  const [customCat, setCustomCat] = useState("");
  const [filterCrop, setFilterCrop] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.crop_id) return toast.error("Ürün seçin");
    if (!(Number(form.amount) > 0)) return toast.error("Tutar 0'dan büyük olmalı");
    const catFinal = form.category === "diğer" && customCat.trim() ? customCat.trim() : form.category;
    try {
      await api.post("/fields/expenses", {
        crop_id: form.crop_id,
        date: form.date,
        category: catFinal,
        amount: Number(form.amount),
        description: form.description,
      });
      toast.success("Gider eklendi ve Finans'a yansıtıldı");
      setForm({ ...form, amount: "", description: "" });
      setCustomCat("");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Gider silinsin mi? (Bağlı Finans satırı da silinir)")) return;
    try {
      await api.delete(`/fields/expenses/${id}`); toast.success("Silindi"); reload();
    } catch (err) { toast.error(err?.message || "Hata"); reload(); }
  };

  const cropName = (cid) => crops.find(c => c.id === cid)?.name ?? "—";
  const filtered = filterCrop ? expenses.filter(e => e.crop_id === filterCrop) : expenses;
  const filteredTotal = useMemo(() => filtered.reduce((s, x) => s + Number(x.amount || 0), 0), [filtered]);

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card>
        <h3 className="heading text-lg mb-3">Gider Ekle</h3>
        {crops.length === 0 ? (
          <div className="text-sm text-[#8a938c]">Önce Genel sekmesinden bir ürün ekleyin.</div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field label="Ürün *">
              <Select required data-testid="fields-exp-crop" value={form.crop_id} onChange={e => setForm({ ...form, crop_id: e.target.value })}>
                <option value="">Seçin...</option>
                {crops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Tarih *"><Input type="date" required data-testid="fields-exp-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Kategori *">
              <Select data-testid="fields-exp-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {categoriesList.map(c => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            {form.category === "diğer" && (
              <Field label="Serbest Kategori (opsiyonel)">
                <Input data-testid="fields-exp-custom-cat" value={customCat} onChange={e => setCustomCat(e.target.value)} placeholder="Örn: nakliye, ilaçlama" />
              </Field>
            )}
            <Field label="Tutar (₺) *"><Input type="number" step="0.01" min="0.01" required data-testid="fields-exp-amount" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Açıklama"><Input data-testid="fields-exp-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Örn: 40 dönüm sulama, 2 gün" /></Field>
            <div className="text-xs text-[#5e675e]">Finans'a otomatik "gider · tarla-{"{ürün}"}" olarak eklenir.</div>
            <Button type="submit" data-testid="fields-exp-submit"><Plus size={14} className="inline mr-1" /> Kaydet</Button>
          </form>
        )}
      </Card>

      <Card className="lg:col-span-2 !p-0 overflow-hidden">
        <div className="p-4 pb-3 border-b border-[#E5E2DC] flex flex-wrap items-center gap-2 justify-between">
          <h3 className="heading text-lg">Gider Geçmişi</h3>
          <div className="flex items-center gap-2">
            <Select data-testid="fields-exp-filter" value={filterCrop} onChange={e => setFilterCrop(e.target.value)} className="!py-1 text-xs">
              <option value="">Tümü</option>
              {crops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <span className="text-xs text-[#8a938c]">Toplam: <b className="text-[#C45B45]">{fmt(filteredTotal)} ₺</b></span>
          </div>
        </div>
        <table className="data-table">
          <thead><tr><th>Tarih</th><th>Ürün</th><th>Kategori</th><th className="text-right">Tutar</th><th>Açıklama</th><th></th></tr></thead>
          <tbody>
            {filtered.slice(0, 200).map(x => (
              <tr key={x.id} data-testid={`fields-exp-row-${x.id}`}>
                <td className="tabnum">{x.date}</td>
                <td>{cropName(x.crop_id)}</td>
                <td><span className="text-xs bg-[#F5F2EC] px-2 py-0.5 rounded">{x.category}</span></td>
                <td className="text-right tabnum font-semibold text-[#C45B45]">{fmt(x.amount)} ₺</td>
                <td className="text-xs text-[#6b746c]">{x.description || "—"}</td>
                <td className="text-right"><button data-testid={`fields-exp-del-${x.id}`} onClick={() => del(x.id)} className="p-1.5 hover:bg-[#fbeee9] text-[#C45B45] rounded"><Trash size={16} /></button></td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={6} className="text-center py-8 text-[#8a938c]">Kayıt yok</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ---------------- HARVESTS TAB ----------------
